<template>
  <div class="top-menu">
    <div id="nav" ref="nav" class="top-menu__nav">
      <el-menu
        :active-text-color="'#ff3176'"
        :background-color="'#ffffff'"
        :default-active="routePath"
        :text-color="'#303133'"
        class="top-menu__menu"
        mode="horizontal"
      >
        <template v-for="(v, index) in menuItems">
          <el-submenu
            v-if="v.children && v.children.length"
            :key="v.url || index"
            :index="String(v.url)"
            ref="menuItems"
            :class="{ 'is-overflow-hidden': isItemHidden(index) }"
            popper-class="top-menu__popper"
          >
            <template #title>
              <span class="top-menu__item-inner">
                <svg-icon :fill="fillColor" class="top-menu__icon" :icon-class="v.icon"></svg-icon>
                <span class="top-menu__label" :title="calcMenuTitle(v)">{{ calcMenuTitle(v) }}</span>
              </span>
            </template>
            <template v-for="(item, indx) in v.children">
              <el-menu-item
                v-if="hasPermission(item.permission_name)"
                :key="index + '-' + indx"
                :index="String(item.url)"
                @click="goToPage(item.url, item)"
              >
                <span class="top-menu__item-inner">
                  <svg-icon :fill="fillColor" class="top-menu__icon" :icon-class="item.icon"></svg-icon>
                  <span class="top-menu__label" :title="item.display_name">{{ item.display_name }}</span>
                </span>
              </el-menu-item>
            </template>
          </el-submenu>
          <el-menu-item
            v-else
            :key="index + 'index'"
            :index="String(v.url)"
            ref="menuItems"
            :class="['top-menu__item', { 'is-overflow-hidden': isItemHidden(index) }]"
            @click="goToPage(v.url, v)"
          >
            <span class="top-menu__item-inner">
              <svg-icon :fill="fillColor" class="top-menu__icon" :icon-class="v.icon"></svg-icon>
              <span class="top-menu__label" :title="calcMenuTitle(v)">{{ calcMenuTitle(v) }}</span>
            </span>
          </el-menu-item>
        </template>

        <!-- 溢出的菜单统一收进「更多」 -->
        <el-submenu
          v-if="isNavOverflow"
          ref="moreMenuItem"
          class="top-menu__more"
          index="__top_menu_more__"
          popper-class="top-menu__popper top-menu__popper--more"
        >
          <template #title>
            <span class="top-menu__item-inner">
              <i class="el-icon-more top-menu__icon"></i>
              <span class="top-menu__label">更多</span>
            </span>
          </template>
          <template v-for="(entry, entryIndex) in overflowMenus">
            <el-menu-item-group
              v-if="entry.isGroup"
              :key="'more-group-' + (entry.menu.url || entryIndex)"
              :title="entry.menu.display_name"
            >
              <el-menu-item
                v-for="(item, childIndex) in entry.children"
                :key="'more-item-' + (item.url || childIndex)"
                :index="String(item.url)"
                @click="goToPage(item.url, item)"
              >
                <span class="top-menu__item-inner">
                  <svg-icon :fill="fillColor" class="top-menu__icon" :icon-class="item.icon"></svg-icon>
                  <span class="top-menu__label" :title="item.display_name">{{ item.display_name }}</span>
                </span>
              </el-menu-item>
            </el-menu-item-group>
            <el-menu-item
              v-else
              :key="'more-direct-' + (entry.menu.url || entryIndex)"
              :index="String(entry.menu.url)"
              @click="goToPage(entry.menu.url, entry.menu)"
            >
              <span class="top-menu__item-inner">
                <svg-icon :fill="fillColor" class="top-menu__icon" :icon-class="entry.menu.icon"></svg-icon>
                <span class="top-menu__label" :title="entry.menu.display_name">{{ entry.menu.display_name }}</span>
              </span>
            </el-menu-item>
          </template>
          <li class="top-menu__more-footer" @click="goToAllMenus">
            <i class="el-icon-s-grid top-menu__icon"></i>
            <span>全部菜单</span>
          </li>
        </el-submenu>
      </el-menu>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'

// 一级菜单之间的间距，需与样式中 el-menu 的 gap 保持一致
const MENU_GAP = 4
// 「更多」菜单未渲染时的宽度兜底值
const MORE_FALLBACK_WIDTH = 88
// 尺寸变化重新计算的防抖时间
const RECALC_DELAY = 120

export default {
  name: 'TopMenu',
  computed: {
    ...mapGetters(['routePath', 'userPermissions', 'isCollapse']),
    // 有权限的一级菜单，保持原顺序
    menuItems() {
      return this.navMenus.filter(v => this.hasPermission(v.permission_name))
    },
    // 当前路由所在的一级菜单下标，用于固定在可见区域
    activeMenuIndex() {
      return this.menuItems.findIndex(menu => this.isMenuActive(menu))
    },
    // 收进「更多」的菜单：有子菜单的按一级菜单分组
    overflowMenus() {
      if (!this.isNavOverflow) return []
      return this.hiddenIndexes
        .slice()
        .sort((a, b) => a - b)
        .map(index => this.menuItems[index])
        .filter(Boolean)
        .map(menu => {
          const isGroup = !!(menu.children && menu.children.length)
          return {
            menu,
            isGroup,
            children: isGroup
              ? menu.children.filter(item => this.hasPermission(item.permission_name))
              : []
          }
        })
        .filter(entry => !entry.isGroup || entry.children.length > 0)
    }
  },
  data() {
    return {
      fillColor: 'currentColor',
      // 导航是否溢出
      isNavOverflow: false,
      // 当前完整展示的一级菜单数量
      visibleCount: 0,
      // 「更多」是否展示
      showMore: false,
      // 被收进「更多」的菜单下标
      hiddenIndexes: []
    }
  },
  props: {
    navMenus: {
      type: Array,
      required: true
    }
  },
  created() {
    this._recalcTimer = null
  },
  mounted() {
    this.$nextTick(() => this.recalcOverflow())

    this._resizeHandler = () => this.scheduleRecalc()
    window.addEventListener('resize', this._resizeHandler)

    if (window.ResizeObserver && this.$refs.nav) {
      this._resizeObserver = new window.ResizeObserver(this._resizeHandler)
      this._resizeObserver.observe(this.$refs.nav)
    }

    // 字体加载完成后文字宽度可能变化，再校准一次
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => {
        if (this.$refs.nav) this.scheduleRecalc()
      }).catch(() => {})
    }
  },
  beforeDestroy() {
    if (this._recalcTimer) clearTimeout(this._recalcTimer)
    window.removeEventListener('resize', this._resizeHandler)
    if (this._resizeObserver) {
      this._resizeObserver.disconnect()
      this._resizeObserver = null
    }
  },
  watch: {
    navMenus() {
      this.scheduleRecalc()
    },
    routePath() {
      // 路由变化会改变子菜单标题与当前项，需要重新计算可见区域
      this.scheduleRecalc()
    },
    userPermissions() {
      this.scheduleRecalc()
    },
    isCollapse() {
      this.scheduleRecalc()
    },
    isNavOverflow: {
      immediate: true,
      handler() {
        this.$emit('isMore', this.isNavOverflow)
      }
    },
    visibleCount: {
      immediate: true,
      handler() {
        this.$emit('update:visibleCount', this.visibleCount)
      }
    }
  },
  methods: {
    scheduleRecalc() {
      if (this._recalcTimer) clearTimeout(this._recalcTimer)
      this._recalcTimer = setTimeout(() => {
        this._recalcTimer = null
        this.recalcOverflow()
      }, RECALC_DELAY)
    },
    getElement(item) {
      return item ? (item.$el || item) : null
    },
    getMenuItemRefs() {
      const refs = this.$refs.menuItems
      if (!refs) return []
      return Array.isArray(refs) ? refs : [refs]
    },
    recalcOverflow() {
      this.$nextTick(() => {
        const nav = this.$refs.nav
        const itemRefs = this.getMenuItemRefs()

        if (!nav || itemRefs.length === 0) {
          this.showMore = false
          this.hiddenIndexes = []
          this.visibleCount = 0
          this.isNavOverflow = false
          return
        }

        // 溢出项用绝对定位隐藏后仍保留宽度，因此每次都可以直接测量
        const widths = itemRefs.map(item => {
          const el = this.getElement(item)
          return el ? el.getBoundingClientRect().width : 0
        })
        const containerWidth = nav.clientWidth
        const validWidths = widths.filter(width => width > 0)

        // 容器或菜单项还未完成布局时不收纳，避免把菜单全部隐藏
        if (!containerWidth || validWidths.length === 0) {
          this.showMore = false
          this.hiddenIndexes = []
          this.visibleCount = 0
          this.isNavOverflow = false
          return
        }

        const totalWidth = widths.reduce((sum, width) => sum + width, 0) +
          Math.max(0, widths.length - 1) * MENU_GAP

        // 全部放得下：不显示「更多」
        if (totalWidth <= containerWidth) {
          this.showMore = false
          this.hiddenIndexes = []
          this.visibleCount = widths.length
          this.isNavOverflow = false
          return
        }

        // 需要「更多」：先让它渲染出来，再按实际宽度计算
        if (!this.showMore) {
          this.showMore = true
          this.$nextTick(() => this.recalcOverflow())
          return
        }

        const moreEl = this.getElement(this.$refs.moreMenuItem)
        const moreWidth = moreEl ? moreEl.getBoundingClientRect().width : MORE_FALLBACK_WIDTH
        // 预留「更多」自身宽度以及它与最后一个可见菜单之间的间距
        const availableWidth = Math.max(0, containerWidth - moreWidth - MENU_GAP)

        // 连一个菜单项 + 「更多」都放不下时，放弃收纳，保证菜单项优先展示
        if (availableWidth < Math.min.apply(null, validWidths)) {
          this.showMore = false
          this.hiddenIndexes = []
          this.visibleCount = widths.length
          this.isNavOverflow = false
          return
        }

        const activeIndex = this.activeMenuIndex

        // 优先保留当前路由所在菜单，其余按原顺序尽可能展示
        let usedWidth = 0
        const visibleIndexes = []
        if (activeIndex > -1) {
          visibleIndexes.push(activeIndex)
          usedWidth = widths[activeIndex]
        }

        for (let i = 0; i < widths.length; i++) {
          if (i === activeIndex) continue
          const needWidth = widths[i] + (visibleIndexes.length > 0 ? MENU_GAP : 0)
          if (usedWidth + needWidth <= availableWidth) {
            visibleIndexes.push(i)
            usedWidth += needWidth
          }
        }

        const hiddenIndexes = []
        for (let i = 0; i < widths.length; i++) {
          if (visibleIndexes.indexOf(i) === -1) hiddenIndexes.push(i)
        }

        this.hiddenIndexes = hiddenIndexes
        this.visibleCount = visibleIndexes.length
        this.isNavOverflow = hiddenIndexes.length > 0
        this.showMore = this.isNavOverflow
      })
    },
    isItemHidden(index) {
      return this.hiddenIndexes.indexOf(index) !== -1
    },
    hasPermission(permissionName) {
      return permissionName ? this.userPermissions.indexOf(permissionName) !== -1 : true
    },
    normalizePath(path) {
      const value = String(path || '').split('?')[0].split('#')[0]
      return value.length > 1 ? value.replace(/\/+$/, '') : value
    },
    // 当前路由是否属于该一级菜单
    isMenuActive(menuItem) {
      const currentPath = this.normalizePath(this.routePath)
      if (!currentPath) return false

      const isMatch = path => {
        const targetPath = this.normalizePath(path)
        if (!targetPath) return false
        return targetPath === currentPath ||
          currentPath.indexOf(targetPath + '/') === 0
      }

      if (menuItem.children && menuItem.children.length) {
        return menuItem.children.some(item =>
          this.hasPermission(item.permission_name) && isMatch(item.url)
        )
      }

      return isMatch(menuItem.url)
    },
    // 计算主菜单的名称
    calcMenuTitle(menuItem) {
      let title = ''
      // 若是子菜单被选中，则显示子菜单名称
      if (menuItem.children && menuItem.children.length > 0) {
        const menu = menuItem.children.find((value) => value.url === this.routePath)
        title = menu ? menu.display_name : menuItem.display_name
      } else {
        title = menuItem.display_name ? menuItem.display_name : ''
      }

      return title
    },
    goToPage(routeName, menu) {
      if (menu.type === 'button') {
        this.$utils.jumpToButtonUrl(menu.url)
        return
      }
      this.$utils.jumpPage(this, routeName, menu)
    },
    goToAllMenus() {
      if (this.$route.name !== 'myNav') {
        this.$router.push({ name: 'myNav' })
      }
    }
  }
}
</script>

<style lang="scss">
/* 顶部菜单：与 Header 同高，菜单项垂直居中 */
.top-menu {
  width: 100%;
  height: 100%;
  min-width: 0;
  display: flex;
  align-items: center;

  &__nav {
    width: 100%;
    height: 100%;
    // 超出部分由右侧「更多菜单」按钮承接
    overflow: hidden;
  }

  &__menu.el-menu--horizontal {
    width: 100%;
    height: 100%;
    padding: 0;
    border-bottom: none;
    display: flex;
    align-items: center;
    // 与 TopMenu.vue 中的 MENU_GAP 保持一致
    gap: 4px;
  }

  // 一级菜单项 / 含子菜单的一级菜单：胶囊式排版
  &__menu.el-menu--horizontal > .el-menu-item,
  &__menu.el-menu--horizontal > .el-submenu {
    float: none;
    flex: 0 0 auto;
    height: 36px;
    line-height: 36px;
    margin: 0;
    padding: 0;
    border: none;
    border-radius: 18px;
    font-size: 14px;
    color: #303133;
    transition: color 0.2s, background-color 0.2s;
  }

  // 被收进「更多」的菜单：脱离文档流并隐藏，但保留宽度用于测量
  &__menu.el-menu--horizontal > .is-overflow-hidden {
    position: absolute !important;
    visibility: hidden !important;
    pointer-events: none !important;
  }

  &__menu.el-menu--horizontal > .el-menu-item {
    padding: 0 14px;
  }

  &__menu.el-menu--horizontal > .el-submenu .el-submenu__title {
    height: 36px;
    line-height: 36px;
    padding: 0 30px 0 14px;
    border: none;
    border-radius: 18px;
    font-size: 14px;
    color: #303133;
    transition: color 0.2s, background-color 0.2s;
  }

  // 悬停
  &__menu.el-menu--horizontal > .el-menu-item:hover,
  &__menu.el-menu--horizontal > .el-menu-item:focus,
  &__menu.el-menu--horizontal > .el-submenu:hover > .el-submenu__title,
  &__menu.el-menu--horizontal > .el-submenu:focus > .el-submenu__title {
    background-color: #f5f7fa !important;
    color: #ff3176;
  }

  // 选中（菜单项背景色由 Element 行内样式指定，需 !important 覆盖）
  &__menu.el-menu--horizontal > .el-menu-item.is-active,
  &__menu.el-menu--horizontal > .el-submenu.is-active > .el-submenu__title {
    background-color: #fff0f6 !important;
    border: none;
    color: #ff3176;
    font-weight: 500;
  }

  // 子菜单箭头
  &__menu.el-menu--horizontal > .el-submenu .el-submenu__title .el-submenu__icon-arrow {
    right: 12px;
    margin-top: -3px;
    font-size: 12px;
  }

  // 图标 + 文案
  &__item-inner {
    max-width: 100%;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    vertical-align: middle;
  }

  &__icon {
    flex: 0 0 18px;
    width: 18px;
    height: 18px;
    margin-right: 6px;
    font-size: 18px;
    line-height: 1;
    text-align: center;
    vertical-align: middle;
  }

  &__label {
    max-width: 160px;
    display: inline-block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    vertical-align: middle;
  }
}

/* 下拉面板渲染在 body 上，通过 popper-class 定向美化 */
.top-menu__popper {
  padding: 0;
  border: none;
  background: transparent;
  box-shadow: none;

  .el-menu--popup {
    min-width: 180px;
    max-width: 320px;
    max-height: 70vh;
    margin-top: 4px;
    padding: 6px;
    overflow-x: hidden;
    overflow-y: auto;
    border: 1px solid #ebeef5;
    border-radius: 10px;
    box-shadow: 0 8px 24px rgba(16, 24, 40, 0.12);
  }

  .el-menu-item {
    height: 36px;
    line-height: 36px;
    padding: 0 12px;
    margin: 2px 0;
    border-radius: 6px;
    font-size: 14px;
    color: #303133;

    &:hover,
    &:focus {
      background-color: #fff5f9 !important;
      color: #ff3176;
    }

    &.is-active {
      background-color: #fff0f6 !important;
      color: #ff3176;
      font-weight: 500;
    }
  }

  .top-menu__item-inner {
    width: 100%;
    justify-content: flex-start;
  }

  .top-menu__label {
    max-width: 230px;
  }
}
</style>
